import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { COMPANY } from "@/data/content";
import { scrollToId } from "@/lib/api";

const LINKS = [
  { id: "about", label: "About", route: "/about" },
  { id: "products", label: "Products" },
  { id: "process", label: "How it works" },
  { id: "industries", label: "Who we fund" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export const Logo = ({ className = "h-10" }) => (
  <img src="/brand/logo.webp" alt="613 Advance" className={`${className} w-auto object-contain`} data-testid="site-logo" />
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const route = LINKS.find((l) => l.id === id)?.route;
    if (route) nav(route);
    else scrollToId(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-white/80 shadow-[0_1px_0_var(--line)] backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("top")} data-testid="nav-logo-btn" aria-label="613 Advance home">
          <Logo className="h-12 sm:h-14" />
        </button>
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="group relative text-sm font-medium text-[var(--ink)]/80 transition-colors hover:text-[var(--ink)]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-left scale-x-0 bg-gradient-to-r from-[var(--blue)] to-[var(--lime)] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={COMPANY.phoneHref} data-testid="nav-phone-link" className="hidden items-center gap-2 font-mono text-sm text-[var(--ink)] md:flex">
            <Phone className="h-4 w-4 text-[var(--green)]" /> {COMPANY.phone}
          </a>
          <button
            onClick={() => go("apply")}
            data-testid="nav-apply-btn"
            className="hidden rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--blue)] sm:block"
          >
            Apply now
          </button>
          <button onClick={() => setOpen((o) => !o)} className="rounded-full p-2 lg:hidden" data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="border-t border-[var(--line)] bg-white px-5 pb-6 pt-2 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l) => (
              <button key={l.id} onClick={() => go(l.id)} data-testid={`mobile-nav-${l.id}`} className="block w-full border-b border-[var(--line)] py-4 text-left font-display text-lg font-bold italic">
                {l.label}
              </button>
            ))}
            <button onClick={() => go("apply")} data-testid="mobile-nav-apply" className="mt-5 w-full rounded-full bg-[var(--blue)] py-3.5 font-semibold text-white">
              Apply now
            </button>
            <a href={COMPANY.phoneHref} className="mt-3 block text-center font-mono text-sm" data-testid="mobile-nav-phone">{COMPANY.phone}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
